import { FaRegularFile, FaSolidBarsStaggered, FaSolidEraser, FaSolidList, FaSolidMagnifyingGlass, FaSolidMountain, FaSolidPaintbrush, FaSolidToolbox, FaSolidUsers } from "solid-icons/fa"
import { Component, createSignal, Match, Show, Switch, useContext } from "solid-js"
import { Engine } from "../../lib/core/engine/Engine.type"
import { SelectedToolId } from "../../lib/core/map_editor/SelectedToolId.enum"
import preventCursorSelectText from "../../lib/dom/event/preventCursorSelectText"
import stopImmediatePropagation from "../../lib/dom/event/stopImmediatePropagation"
import { EventResult } from "../../lib/event/event"
import { InputActionType } from "../../lib/event/InputRouter/InputActionType"
import { mountInputActionListenerWhen } from "../../lib/event/InputRouter/mountInputActionListener"
import Button from "../../lib/jsx/Button"
import useIsActionLogListModalOpenSignal from "../../rx/shared/ModalMenu/useIsActionLogListModalOpenSignal"
import { useSelectedToolSignal } from "../../rx/signal/useSelectedToolSignal"
import EngineContext from "../EngineContext"
import ElevationTool from "../MapEditor/ElevationTool/ElevationTool"
import EraseTileSelect from "../MapEditor/EraseTileSelect"
import FileWidget from "../MapEditor/FileWidget"
import InspectTileWidget from "../MapEditor/InspectTileWidget"
import PaintTileSelect from "../MapEditor/PaintTileSelect"
import PlayersWidget from "../MapEditor/PlayersWidget"
import ToolsWidget from "../MapEditor/ToolsWidget/ToolsWidget"

const MapEditorToolbar: Component = () => {
  const engine: Engine = useContext(EngineContext)

  const [isMenuOpen, setIsMenuOpen] = createSignal(false)

  const [getSelectedTool, setSelectedTool] = useSelectedToolSignal(engine)
  const [isActionLogListModalOpen, setIsActionLogListModalOpen] = useIsActionLogListModalOpenSignal()

  function setSelectTool(newValue: SelectedToolId) {
    return (/*event: MouseEvent*/) => {
      // console.log('setSelectTool', newValue)
      setSelectedTool(newValue)
      setIsMenuOpen(false)
    }
  }

  function is(id: SelectedToolId): boolean {
    return getSelectedTool() === id
  }

  // eslint-disable-next-line solid/reactivity
  mountInputActionListenerWhen(InputActionType.Escape, isMenuOpen, () => {
    if (isMenuOpen()) {
      setIsMenuOpen(false)
      return EventResult.Stop
    }
  })

  return <div
    ref={(elem) => {
      preventCursorSelectText(elem)
    }}
    id="hudmet"
    class="card widget top-left"
    onWheel={stopImmediatePropagation}
    onMouseUp={stopImmediatePropagation}
    onMouseDown={stopImmediatePropagation}
    onClick={stopImmediatePropagation}
    onContextMenu={stopImmediatePropagation}
    onMouseMove={stopImmediatePropagation}
  >
    <div class="card-body">
      <div classList={{ 'btn-group-vertical': isMenuOpen(), 'btn-group': !isMenuOpen() }} class="text-nowrap">
        {!isMenuOpen() && <Button name="menu"
          class="btn btn-secondary w-100"
          classList={{ 'd-none': isMenuOpen() }}
          disabled={isMenuOpen()}
          onClick={() => setIsMenuOpen(!isMenuOpen())}
          aria-label="Menu"
        ><FaSolidBarsStaggered /> Menu</Button>}
        {(isMenuOpen() || is(SelectedToolId.Inspect)) && <Button name="inspect"
          class="btn btn-secondary w-100"
          classList={{ 'active': isMenuOpen() && is(SelectedToolId.Inspect) }}
          disabled={!isMenuOpen() && is(SelectedToolId.Inspect)}
          onClick={setSelectTool(SelectedToolId.Inspect)}
          aria-label="Inspect"
        ><FaSolidMagnifyingGlass /> Inspect</Button>}
        {(isMenuOpen() || is(SelectedToolId.File)) && <Button name="file"
          class="btn btn-secondary w-100"
          classList={{ 'active': isMenuOpen() && is(SelectedToolId.File) }}
          disabled={!isMenuOpen() && is(SelectedToolId.File)}
          onClick={setSelectTool(SelectedToolId.File)}
          aria-label="File"
        ><FaRegularFile /> File</Button>}
        {(isMenuOpen() || is(SelectedToolId.Tools)) && <Button name="tools"
          class="btn btn-secondary w-100"
          classList={{ 'active': isMenuOpen() && is(SelectedToolId.Tools) }}
          disabled={!isMenuOpen() && is(SelectedToolId.Tools)}
          onClick={setSelectTool(SelectedToolId.Tools)}
          aria-label="Tools"
        ><FaSolidToolbox /> Tools</Button>}
        {(isMenuOpen() || is(SelectedToolId.Paint)) && <Button name="paint"
          class="btn btn-secondary w-100"
          classList={{ 'active': isMenuOpen() && is(SelectedToolId.Paint) }}
          disabled={!isMenuOpen() && is(SelectedToolId.Paint)}
          onClick={setSelectTool(SelectedToolId.Paint)}
          aria-label="Paint"
        ><FaSolidPaintbrush /> Paint</Button>}
        {(isMenuOpen() || is(SelectedToolId.Erase)) && <Button name="erase"
          class="btn btn-secondary w-100"
          classList={{ 'active': isMenuOpen() && is(SelectedToolId.Erase) }}
          disabled={!isMenuOpen() && is(SelectedToolId.Erase)}
          onClick={setSelectTool(SelectedToolId.Erase)}
          aria-label="Erase"
        ><FaSolidEraser /> Erase</Button>}
        {(isMenuOpen() || is(SelectedToolId.Players)) && <Button name="players"
          class="btn btn-secondary w-100"
          classList={{ 'active': isMenuOpen() && is(SelectedToolId.Players) }}
          disabled={!isMenuOpen() && is(SelectedToolId.Players)}
          onClick={setSelectTool(SelectedToolId.Players)}
          aria-label="Players"
        ><FaSolidUsers /> Players</Button>}
        {(isMenuOpen() || is(SelectedToolId.Elevation)) && <Button name="elevation"
          class="btn btn-secondary w-100"
          classList={{ 'active': isMenuOpen() && is(SelectedToolId.Elevation) }}
          disabled={!isMenuOpen() && is(SelectedToolId.Elevation)}
          onClick={setSelectTool(SelectedToolId.Elevation)}
          aria-label="Elevation"
        ><FaSolidMountain /> Elevation</Button>}
        {isMenuOpen() && <Button name="action-log"
          class="btn btn-secondary w-100"
          classList={{ 'active': isActionLogListModalOpen() }}
          onClick={() => {
            setIsActionLogListModalOpen(!isActionLogListModalOpen())
            setIsMenuOpen(false)
          }}
          aria-label="Action Log"
        ><FaSolidList /> Action Log</Button>}
      </div>
    </div>
    <Show when={!isMenuOpen()}>
      {/* <Dynamic component={getSelectedTool()} /> */}
      <Switch>
        <Match when={is(SelectedToolId.File)}>
          <FileWidget />
        </Match>
        <Match when={is(SelectedToolId.Inspect)}>
          <InspectTileWidget />
        </Match>
        <Match when={is(SelectedToolId.Tools)}>
          <ToolsWidget />
        </Match>
        <Match when={is(SelectedToolId.Paint)}>
          <PaintTileSelect />
        </Match>
        <Match when={is(SelectedToolId.Erase)}>
          <EraseTileSelect />
        </Match>
        <Match when={is(SelectedToolId.Players)}>
          <PlayersWidget />
        </Match>
        <Match when={is(SelectedToolId.Elevation)}>
          <ElevationTool />
        </Match>
      </Switch>
    </Show>
  </div>
}

export default MapEditorToolbar
